import type {
  ChainFailure,
  ConsumerRow,
  IdentityProfile,
  OperatorLifecycleCount,
  PlatformActivityData,
  PlatformActivityDay,
  PlatformBillingRow,
  PlatformOverviewData,
} from './client.ts';
import { localDayOf, formatCount, formatDateTime, formatDay, formatMoney, freshness, stripRuleReferences } from '@affdice/portal-kit';
import type { SortDir, SortState } from '@affdice/portal-kit';
import { t, tCount } from './messages.ts';

/**
 * What the AffDice SuperAdmin shows.
 *
 * Every screen here answers a question about work the platform has or has not
 * done — whether the audit chain still verifies, whether a consumer has
 * quietly stopped, how much is reaching it at all. None of them is an
 * operator's business, and the first two fail silently by nature: an altered
 * record throws nothing, and a stalled cursor throws nothing.
 */

/* ------------------------------------------------------------------ *
 * §42.3 — the chain
 * ------------------------------------------------------------------ */

export interface ChainView {
  readonly verified: boolean;
  readonly verdict: string;
  readonly failures: readonly ChainFailure[];
  /** Set when there is nothing to verify, which is not the same as verified. */
  readonly emptyWarning?: string;
  /**
   * Item 1 (third batch) — the two counts as their own figures, not only
   * buried inside the verdict sentence. "Verified" over 4 records and
   * "verified" over 400,000 are the same words and very different facts.
   */
  readonly recordsChecked: string;
  readonly chainsChecked: string;
  /**
   * §42.3 — when the hashes were recomputed, as the server timed it.
   *
   * Absent when the server did not send one, which is how an older build
   * answers. The screen says nothing rather than stamping itself with the
   * moment the page was drawn: this is the one screen whose entire claim is
   * about a point in time, and a time it invented would be the wrong one.
   */
  readonly verifiedAt?: string;
}

/**
 * §42.3 — the verdict, with its numbers.
 *
 * A green tick over an empty table is exactly the failure this check exists to
 * avoid: "verified" and "verified nothing" look identical, and the second one
 * is what a truncated audit log produces.
 */
export function chain(result: {
  failures: ChainFailure[];
  records_checked: string;
  chains_checked: string;
  verdict: string;
  /** §42.3 — when the hashes were recomputed. */
  verified_at?: string;
}): ChainView {
  const nothing = result.records_checked === '0';
  return {
    verified: result.failures.length === 0 && !nothing,
    verdict: result.verdict,
    failures: result.failures,
    // Batch 2 item 12 — the server sends these as plain digit strings
    // ("18412"), so the count read as code rather than a number anyone
    // could read at a glance.
    recordsChecked: formatCount(Number(result.records_checked)),
    chainsChecked: formatCount(Number(result.chains_checked)),
    // Passed through rather than defaulted to now: a page that stamps itself
    // says when it was drawn, which is the one thing this screen must not
    // claim. An older build's answer carries no time, and says so.
    ...(result.verified_at ? { verifiedAt: formatDateTime(result.verified_at) } : {}),
    ...(nothing
      ? {
          emptyWarning: t('view.chain.empty'),
        }
      : {}),
  };
}

/* ------------------------------------------------------------------ *
 * §19.6 — consumers
 * ------------------------------------------------------------------ */

export interface ConsumerLine {
  readonly name: string;
  readonly lastMoved: string;
  readonly stalledHours: number;
  readonly failed: number;
  readonly lastError: string | null;
  /**
   * Events the cursor has reached, for the brand chosen or for every brand —
   * or a dash for a job that does not read events, which keeps the row as a
   * heartbeat and has no position in the events at all.
   */
  readonly calls: string;
  /** Events still ahead of it; a dash for a job that does not read events, as above. */
  readonly waiting: string;
  readonly state: 'MOVING' | 'NOT_STARTED' | 'STALLED' | 'FAILING';
  readonly note?: string;
}

/** A cursor that has not moved in this long is not busy. */
export const STALL_HOURS = 6;

/**
 * §19.6, §45.7b — the only sign of a consumer that has stopped.
 *
 * Nothing throws when one stalls. The events simply stop being processed, and
 * the commission they would have produced is not late — it is absent.
 */
/**
 * The consumers that need a look, first.
 *
 * This screen exists to find the one worker that has stopped, and it listed
 * 37 of them in server order down 1,730px — with a "3 not moving" chip at the
 * top and no way to tell which three without reading every row.
 *
 * Deliberately *not* paged, which is the other answer to a long table: a
 * pager on a health screen can put the failing consumer on page two, and a
 * screen whose whole job is "is anything wrong" must not be able to hide the
 * thing that is wrong. Ordering costs nothing and puts it on the first line.
 *
 * FAILING before STALLED because a consumer with failures is losing work now,
 * where a stalled one may simply have had nothing to do. NOT_STARTED after
 * those two: it is ticking, so it is not lost, but it has events waiting and
 * has reached none of them. Within a group the server's order is kept, so the
 * list does not reshuffle between polls.
 */
const CONSUMER_ATTENTION: Record<ConsumerLine['state'], number> = {
  FAILING: 0,
  STALLED: 1,
  NOT_STARTED: 2,
  MOVING: 3,
};

export function consumersNeedingAttentionFirst(
  lines: readonly ConsumerLine[],
): readonly ConsumerLine[] {
  return [...lines].sort((a, b) => CONSUMER_ATTENTION[a.state] - CONSUMER_ATTENTION[b.state]);
}

export function consumerLine(row: ConsumerRow, now: Date): ConsumerLine {
  const hours = Math.max(
    0,
    Math.floor((now.getTime() - new Date(row.updated_at).getTime()) / 3_600_000),
  );
  const stalled = hours >= STALL_HOURS;
  const failing = row.failed_count > 0;
  /*
   * QA round 2 item I1 — 23 rows read "Calls 0 / Waiting 172 — Moving" on
   * staging, and they were not stuck. Of the 26 worker jobs only the three
   * event readers (attribution, activity-projection, player-lifecycle) ever
   * write last_event_id; the other 23 are scheduled sweeps that use their row
   * as a heartbeat and never read the events at all. With no cursor the server
   * counts every event as waiting, so each of them showed the whole ledger
   * backlog. A dash says what is true: it has no place in the events.
   *
   * The server says which rows read events (reads_events), because a null
   * cursor is what a reader that never advanced and a sweep both have, so the
   * cursor cannot tell them apart. A reader ticking on a null cursor has
   * reached none of the events and reads NOT_STARTED — the case this screen
   * exists for. It would never read STALLED: every clean tick heartbeats the
   * row, so updated_at keeps moving.
   */
  const readsEvents = row.reads_events;
  const notStarted = readsEvents && row.calls === 0 && row.waiting > 0;

  return {
    name: row.consumer,
    lastMoved: formatDateTime(row.updated_at),
    stalledHours: hours,
    failed: row.failed_count,
    lastError: row.last_error,
    calls: readsEvents ? formatCount(row.calls) : '—',
    waiting: readsEvents ? formatCount(row.waiting) : '—',
    state: failing ? 'FAILING' : stalled ? 'STALLED' : notStarted ? 'NOT_STARTED' : 'MOVING',
    ...(failing
      ? {
          note: tCount('view.consumer.failed', row.failed_count),
        }
      : stalled
        ? {
            note: t('view.consumer.stalled', { hours }),
          }
        : notStarted
          ? {
              note: tCount('view.consumer.notStarted', row.waiting, { waiting: formatCount(row.waiting) }),
            }
          : {}),
  };
}

/* ------------------------------------------------------------------ *
 * Suspend — by operator, or by brand
 * ------------------------------------------------------------------ */

/** One enrolment, named so a checkbox can hold it: this profile, this brand. */
export function enrollmentKey(affiliateId: string, brandId: string): string {
  return `${affiliateId}:${brandId}`;
}

export interface SuspensionTarget {
  readonly operatorId: string;
  readonly operator: string;
  readonly affiliateId: string;
  readonly brandId: string;
  readonly brand: string;
}

/**
 * What Suspend on chosen brands will actually do, before it is pressed: the
 * per-brand suspension the enrolment table already offers, once per brand.
 * An enrolment that is already suspended is counted and left alone, so the
 * button never claims more than it will change.
 */
export function suspensionPlan(
  profiles: readonly IdentityProfile[],
  chosen: ReadonlySet<string>,
): { targets: SuspensionTarget[]; alreadySuspended: number; operators: number } {
  const targets: SuspensionTarget[] = [];
  let alreadySuspended = 0;
  for (const p of profiles) {
    for (const b of p.brands) {
      if (!chosen.has(enrollmentKey(p.affiliate_id, b.brand_id))) continue;
      if (b.state !== 'ACTIVE') {
        alreadySuspended += 1;
        continue;
      }
      targets.push({
        operatorId: p.operator_id,
        operator: p.operator,
        affiliateId: p.affiliate_id,
        brandId: b.brand_id,
        brand: b.brand,
      });
    }
  }
  return { targets, alreadySuspended, operators: new Set(targets.map((target) => target.operatorId)).size };
}

export interface DeactivationTarget {
  readonly operatorId: string;
  readonly operator: string;
  readonly affiliateId: string;
}

/**
 * What Suspend on chosen operators will do: deactivate this person at each
 * operator ticked — the operator-wide status, every brand there at once, the
 * same change the status chip makes. Suspending each brand instead left the
 * person reading as active at an operator where they could do nothing. Only
 * an ACTIVE profile can move to DEACTIVATED, so any other is left out rather
 * than sent to be refused.
 */
export function deactivationPlan(
  profiles: readonly IdentityProfile[],
  chosen: ReadonlySet<string>,
): { targets: DeactivationTarget[] } {
  return {
    targets: profiles
      .filter((p) => chosen.has(p.operator_id) && p.status === 'ACTIVE')
      .map((p) => ({ operatorId: p.operator_id, operator: p.operator, affiliateId: p.affiliate_id })),
  };
}

/* ------------------------------------------------------------------ *
 * Onboarding — Ch. 50
 * ------------------------------------------------------------------ */

export interface ConditionLine {
  readonly no: number;
  readonly what: string;
  readonly met: boolean;
  readonly waivable: boolean;
  readonly waivedBecause: string | null;
  readonly note?: string;
}

/**
 * §50.5a — the gate as a checklist, not a form.
 *
 * "Each condition is queryable, each shows pass or fail with the reason, and
 * the operator sees the same list the platform does." So an unmet condition
 * shows what it is waiting for rather than an empty tick box, and the two that
 * cannot be waived say so on their own row — a list where the exception lives
 * in a footnote is one somebody waives from memory.
 */
export function conditionLine(row: {
  condition_no: number;
  is_met: boolean;
  detail: string;
  waiver_reason: string | null;
  waivable: boolean;
}): ConditionLine {
  return {
    no: row.condition_no,
    // Item 6 (third batch) — the backend's own condition text carries
    // "(§41.12)"-style citations, and this console rendered them raw. The
    // AffDice Admin has stripped them at its own boundary since it was
    // written; nothing here ever imported the same helper.
    what: stripRuleReferences(row.detail),
    met: row.is_met,
    waivable: row.waivable,
    waivedBecause: row.waiver_reason,
    ...(row.waivable
      ? {}
      : {
          note:
            row.condition_no === 3
              ? t('view.condition.noWaiveFeed')
              : t('view.condition.noWaiveRetention'),
        }),
  };
}

export interface GateView {
  readonly lines: readonly ConditionLine[];
  readonly met: number;
  readonly total: number;
  readonly verdict: string;
  readonly canGoLive: boolean;
}

export function goLiveGate(rows: Parameters<typeof conditionLine>[0][]): GateView {
  const lines = rows.map(conditionLine);
  const met = lines.filter((l) => l.met).length;
  const blockedByUnwaivable = lines.filter((l) => !l.met && !l.waivable);

  return {
    lines,
    met,
    total: lines.length,
    canGoLive: met === lines.length,
    verdict:
      met === lines.length
        ? t('view.gate.allPass')
        : blockedByUnwaivable.length > 0
          ? t('view.gate.blocked', { met, total: lines.length, blocked: blockedByUnwaivable.length })
          : t('view.gate.waivable', { met, total: lines.length }),
  };
}

/**
 * §50.2 — what each lifecycle state means for billing and traffic.
 *
 * Item 6 (third batch) — this no longer repeats the state's own name. The
 * screen shows the name (`lifecycleLabel`) immediately before this sentence,
 * so leading with it produced "Live. Live. Events are billed…". Rule numbers
 * are gone from the prose for the same reason `stripRuleReferences` exists:
 * "(§47.6)" on screen is a citation the reader cannot follow.
 */
export function lifecycleMeaning(state: string): string {
  switch (state) {
    case 'PROSPECT':
    case 'CONFIGURING':
      return t('view.lifecycle.meaning.setup');
    case 'LIVE':
      return t('view.lifecycle.meaning.live');
    case 'RESTRICTED':
      return t('view.lifecycle.meaning.restricted');
    case 'SUSPENDED':
      // Item 7 (third batch) — previously unreachable (nothing ever wrote
      // this state), so this text was never actually seen until now.
      return t('view.lifecycle.meaning.suspended');
    default:
      return state;
  }
}

/** Gross on a platform invoice row — formatting lives here, not in App.tsx (§1.3a). */
export function platformInvoiceGross(row: {
  gross_minor: string;
  currency: string;
}): string {
  return formatMoney({ amount_minor: row.gross_minor, currency: row.currency });
}

/**
 * Everything a platform invoice popup can honestly say.
 *
 * Every line comes off the list row the invoices table already holds. There is
 * no per-invoice read on the platform side — the only other thing the API
 * offers for one of these is the settlement mark that records it paid — so the
 * popup states what is genuinely known rather than implying a document it
 * cannot open. Net, tax and gross are three separate facts and are shown as
 * three: a reader querying a bill is querying the difference between them, and
 * a popup that carried only the total would send them back to the table.
 *
 * The currency is not a row of its own: it is already printed on each of the
 * three amounts, and a fourth line repeating it reads as a fourth figure.
 */
export function platformInvoiceFacts(row: {
  number: string;
  period_start: string;
  period_end: string;
  net_minor: string;
  tax_minor: string;
  gross_minor: string;
  currency: string;
  state: string;
}): readonly { readonly label: string; readonly value: string }[] {
  const money = (amount_minor: string) => formatMoney({ amount_minor, currency: row.currency });
  return [
    { label: t('view.invoice.number'), value: row.number },
    // §22.1 — bare `YYYY-MM-DD` from billing.ts, not an instant.
    { label: t('common.period'), value: t('view.invoice.period', { start: formatDay(row.period_start), end: formatDay(row.period_end) }) },
    { label: t('view.invoice.net'), value: money(row.net_minor) },
    { label: t('view.invoice.tax'), value: money(row.tax_minor) },
    { label: t('view.invoice.gross'), value: money(row.gross_minor) },
    { label: t('common.state'), value: row.state },
  ];
}

/**
 * Batch 2 review, finding 5 — a word for the affiliate's eight ledger
 * statuses (§6.3, 0013_affiliate_statuses.sql), for the chip that shows the
 * raw code today. AMS has no existing word map for the full eight (only
 * `affiliate_portal_status`'s three portal words, which collapse states this
 * console needs kept apart), so this title-cases the code — every one of the
 * eight reads cleanly that way (`UNDER_REVIEW` → "Under Review").
 */
export function affiliateStatusLabel(status: string): string {
  switch (status) {
    case 'PENDING': return t('view.affiliateStatus.pending');
    case 'ACTIVE': return t('view.affiliateStatus.active');
    case 'UNDER_REVIEW': return t('view.affiliateStatus.underReview');
    case 'SUSPENDED': return t('view.affiliateStatus.suspended');
    case 'BLOCKED': return t('view.affiliateStatus.blocked');
    case 'REJECTED': return t('view.affiliateStatus.rejected');
    case 'TERMINATED': return t('view.affiliateStatus.terminated');
    case 'DEACTIVATED': return t('view.affiliateStatus.deactivated');
    default:
      // A code this console has no word for yet still reads cleanly title-cased.
      return status
        .toLowerCase()
        .split('_')
        .map((w) => (w.length === 0 ? w : w[0]!.toUpperCase() + w.slice(1)))
        .join(' ');
  }
}

/**
 * Batch 2 review, finding 5 — the chip tone for an affiliate's own ledger
 * status, by the DESIGN §4 table: `locked` only for the healthy state,
 * `overdue` only for the three the ledger stops future accrual for and calls
 * a for-cause close (BLOCKED, REJECTED, TERMINATED — §6.3d), and `neutral`
 * for the rest — PENDING, UNDER_REVIEW and SUSPENDED are a review, not a
 * verdict (§6.3a: they keep existing players earning, same as ACTIVE), and
 * DEACTIVATED is "a deliberate close by either side", not one for cause.
 * Never `overdue` for all seven non-ACTIVE states at once, which read every
 * one of them — including a plain pending application — as a failure.
 */
const AFFILIATE_STATUS_TONE: Readonly<Record<string, 'locked' | 'neutral' | 'overdue'>> = {
  ACTIVE: 'locked',
  BLOCKED: 'overdue',
  REJECTED: 'overdue',
  TERMINATED: 'overdue',
};

export function affiliateStatusTone(status: string): 'locked' | 'neutral' | 'overdue' {
  return AFFILIATE_STATUS_TONE[status] ?? 'neutral';
}

/**
 * The status chip in the AMS affiliate popup, as the control that changes it.
 *
 * The chip and the button it replaces were always bound to the same fact —
 * `affiliates.status`, this person's state on this one operator. The chip
 * prints the portal-facing word for that state (three words cover eight ledger
 * states) and the button changed the state itself; nothing is being conflated
 * by making one the control for the other.
 *
 * It stays a plain chip outside the two states the button was ever offered
 * for. The other six are their own operator's call, and a control that looks
 * pressable and refuses is worse than a fact that never claimed to be one.
 *
 * The visible word is the state alone; the sentence is the accessible name and
 * the tooltip, state first and action second. A control carrying only its
 * action leaves the reader working out which way round they are.
 */
export function affiliateActiveToggle(row: {
  status: string;
  shown_to_affiliate: string;
}): {
  readonly label: string;
  readonly tone: 'locked' | 'neutral' | 'overdue';
  readonly canToggle: boolean;
  readonly to: 'ACTIVE' | 'DEACTIVATED' | null;
  readonly description: string;
} {
  const label = row.shown_to_affiliate;
  // Batch 2 review, finding 5 — this used to be locked/provisional off the
  // three-word portal label, which put "Under Review" and "Ended" under the
  // same still-moving mark (the console's own accent since §12.1, a fixed
  // amber before it) as a draft money total. Ties to the real ledger status
  // instead, so the tone matches the one on the Status column.
  const tone = affiliateStatusTone(row.status);
  if (row.status === 'ACTIVE') {
    return { label, tone, canToggle: true, to: 'DEACTIVATED',
      description: t('view.toggle.deactivate', { label }) };
  }
  if (row.status === 'DEACTIVATED') {
    return { label, tone, canToggle: true, to: 'ACTIVE',
      description: t('view.toggle.reactivate', { label }) };
  }
  return { label, tone, canToggle: false, to: null,
    description: t('view.toggle.theirs', { label }) };
}

/**
 * A headline money figure on the operator dashboard, and on the same read
 * narrowed to one brand.
 *
 * These arrive as minor units with the currency beside them, and both callers
 * were pasting the two together — `GBP 160000` where the figure is £1,600.00.
 * Formatting belongs here rather than in the components for the reason every
 * other money helper in this file is here, and it is one function rather than
 * two so an operator's figure and one of its brands' figures can never be
 * printed in two different shapes side by side.
 *
 * A missing amount, or a period with no currency to state one in, is a dash:
 * an unavailable figure rendered as zero is the failure this console exists
 * to make visible, reproduced in the console itself.
 */
export function operatorFigureMoney(amountMinor: string | null, currency: string | null): string {
  if (amountMinor == null || currency == null) return '—';
  return formatMoney({ amount_minor: amountMinor, currency });
}

/** What `overview()` answers with — the operator's own dashboard read. */
export interface OperatorFigureData {
  readonly clicks: number;
  readonly customers: number;
  readonly ftds: number;
  readonly registration_cr_pct: number | null;
  readonly paid_minor: string;
  readonly outstanding_payable_minor: string;
  readonly currency: string | null;
  readonly deposits_minor: string | null;
  readonly deposits_count: number | null;
  readonly ftd_amount_minor: string | null;
  readonly activities: number | null;
  readonly ggr_minor: string | null;
  readonly ngr_minor: string | null;
}

export interface OperatorFigureGroup {
  readonly title: string;
  readonly figures: readonly { readonly label: string; readonly value: string }[];
  readonly note?: string;
}

/**
 * A money average — minor units over a plain count. Nothing to divide by is a
 * dash, not a division producing Infinity or a zero that reads as a real one:
 * an average of nothing is not an average of zero.
 */
function operatorFigureAverage(
  numeratorMinor: string | null,
  denominator: number | null,
  currency: string | null,
): string {
  if (numeratorMinor == null || currency == null || denominator == null || denominator <= 0) return '—';
  const num = BigInt(numeratorMinor);
  const den = BigInt(denominator);
  // Half away from zero, symmetrically, as every other money figure rounds
  // (§21.6a). BigInt division truncates toward zero, so a negative NGR fed
  // straight through would round the other way.
  const negative = num < BigInt(0);
  const q = ((negative ? -num : num) + den / BigInt(2)) / den;
  return formatMoney({ amount_minor: (negative ? -q : q).toString(), currency });
}

/**
 * The operator's own dashboard, group for group — the same figures, and the
 * same four averages, that `dashboardOverview` in the AffDice Admin draws
 * from this same envelope, so support on the phone and the operator at their
 * desk are reading one list. The averages are derived here, never fetched: an
 * average that could disagree with the totals above it is worse than none.
 */
export function operatorFigureGroups(d: OperatorFigureData): OperatorFigureGroup[] {
  const money = (v: string | null) => operatorFigureMoney(v, d.currency);
  const count = (v: number | null) => (v == null ? '—' : formatCount(v));
  const avg = (n: string | null, den: number | null) => operatorFigureAverage(n, den, d.currency);
  const averages = [
    { label: t('view.figure.avgFtd'), value: avg(d.ftd_amount_minor, d.ftds) },
    { label: t('view.figure.avgDeposit'), value: avg(d.deposits_minor, d.deposits_count) },
    { label: t('view.figure.ggrPerCustomer'), value: avg(d.ggr_minor, d.customers) },
    { label: t('view.figure.ggrMinusBonusPerCustomer'), value: avg(d.ngr_minor, d.customers) },
  ];
  return [
    {
      title: t('view.group.earnings'),
      figures: [
        { label: t('view.figure.outstanding'), value: money(d.outstanding_payable_minor) },
        { label: t('view.figure.paid'), value: money(d.paid_minor) },
      ],
    },
    {
      title: t('view.group.insights'),
      figures: [
        { label: t('view.figure.clicks'), value: count(d.clicks) },
        { label: t('view.figure.registrations'), value: count(d.customers) },
        { label: t('view.figure.ftds'), value: count(d.ftds) },
        { label: t('view.figure.activities'), value: count(d.activities) },
      ],
    },
    {
      title: t('view.group.conversion'),
      figures: [
        { label: t('view.figure.registrationCr'), value: d.registration_cr_pct == null ? '—' : `${d.registration_cr_pct}%` },
        { label: t('view.figure.totalFtdAmount'), value: money(d.ftd_amount_minor) },
      ],
    },
    {
      title: t('view.group.transactions'),
      figures: [
        { label: t('view.figure.deposits'), value: money(d.deposits_minor) },
        { label: t('view.figure.totalDepositsCount'), value: count(d.deposits_count) },
      ],
    },
    {
      title: t('view.group.revenue'),
      figures: [
        { label: t('view.figure.ggr'), value: money(d.ggr_minor) },
        { label: t('view.figure.ggrMinusBonus'), value: money(d.ngr_minor) },
      ],
    },
    {
      title: t('view.group.averages'),
      figures: averages,
      ...(averages.every((f) => f.value === '—')
        ? { note: t('view.group.noAverages') }
        : {}),
    },
  ];
}

/**
 * The affiliates the platform holds against one brand.
 *
 * Derived from the operator's own affiliate list rather than fetched: that
 * list already carries each affiliate's brand enrollments, and there is no
 * brand-scoped affiliate read on the platform side to call instead. Matching
 * on the brand's id, not its name — two operators may name a brand the same
 * thing and one operator may rename one.
 */
export function affiliatesOnBrand<T extends { brands: readonly { brand_id: string }[] }>(
  rows: readonly T[],
  brandId: string,
): readonly T[] {
  return rows.filter((row) => row.brands.some((b) => b.brand_id === brandId));
}

/**
 * Item 6 (third batch) — an affiliate invoice's two money columns, for the
 * AMS affiliate popup. Same reason as `platformInvoiceGross` above: §1.3a
 * keeps money formatting out of the components.
 */
export function affiliateInvoiceAmounts(row: {
  total_minor: string;
  settled_minor: string;
  currency: string;
}): { total: string; settled: string } {
  return {
    total: formatMoney({ amount_minor: row.total_minor, currency: row.currency }),
    settled: formatMoney({ amount_minor: row.settled_minor, currency: row.currency }),
  };
}

/**
 * Batch 4 item 2 — an affiliate's commission on the AMS list. Formatting lives
 * here, not in the component (§1.3a), same as every other money column.
 */
export function affiliateCommission(row: {
  commission_minor: string;
  currency: string;
}): string {
  return formatMoney({ amount_minor: row.commission_minor, currency: row.currency });
}

/** Item 6 (third batch) — a dispute's disputed amount, which may be absent. */
export function disputedAmount(row: {
  disputed_amount_minor: string | null;
  currency: string | null;
}): string {
  if (row.disputed_amount_minor == null || row.currency == null) return '—';
  return formatMoney({ amount_minor: row.disputed_amount_minor, currency: row.currency });
}

/* ------------------------------------------------------------------ *
 * AffDice SuperAdmin Dashboard — §8.6 (Architecture)
 *
 * The cross-tenant counterpart of `affdice-admin`'s Dashboard. Three of its
 * four groups are not new numbers: they reuse `markets()`, `chain()` and
 * `consumerLine()` above, on data the Markets/Audit/Consumers tabs already
 * fetch — a platform-wide summary that recomputed those would be a second
 * definition of the same question, which is the failure §0.6a is written
 * against. Only tenant portfolio and platform billing are genuinely new
 * figures, because nothing else on this console answers them.
 * ------------------------------------------------------------------ */

export interface PlatformKpiFigure {
  readonly label: string;
  readonly value: string;
  readonly tone?: 'note' | 'caution';
  /** Which activity figure this is, so a screen need not read it off the translated label. */
  readonly metric?: 'clicks' | 'registrations' | 'ftds';
  /** Daily series for a sparkline — omitted when fewer than two points. */
  readonly spark?: readonly number[];
}

function sparkSeries(
  daily: readonly PlatformActivityDay[],
  key: keyof Pick<PlatformActivityDay, 'clicks' | 'registrations' | 'ftds' | 'operators' | 'affiliates'>,
): readonly number[] | undefined {
  if (daily.length < 2) return undefined;
  return daily.map((d) => d[key]);
}

function withSpark(
  figure: Omit<PlatformKpiFigure, 'spark'>,
  spark: readonly number[] | undefined,
): PlatformKpiFigure {
  return spark ? { ...figure, spark } : figure;
}

export interface PlatformBillingLine {
  readonly currency: string;
  readonly issued: string;
  readonly paid: string;
  readonly outstanding: string;
}

export interface PlatformDashboardView {
  readonly updated: string;
  readonly tenantPortfolio: readonly PlatformKpiFigure[];
  readonly goLive: readonly PlatformKpiFigure[];
  readonly health: readonly PlatformKpiFigure[];
  readonly activity: readonly PlatformKpiFigure[];
  /** Set only once the Layer C rollup has never run — §39.2a. */
  readonly activityNote?: string;
  readonly billing: readonly PlatformBillingLine[];
  readonly billingNote: string;
}

/** §50.2 — short label for a lifecycle state in tables and counts. */
export function lifecycleLabel(state: string): string {
  switch (state) {
    case 'PROSPECT': return t('view.lifecycle.label.prospect');
    case 'CONFIGURING': return t('view.lifecycle.label.configuring');
    case 'LIVE': return t('view.lifecycle.label.live');
    case 'RESTRICTED': return t('view.lifecycle.label.restricted');
    case 'SUSPENDED': return t('view.lifecycle.label.suspended');
    case 'ABANDONED': return t('view.lifecycle.label.abandoned');
    case 'SOFT_DELETED': return t('view.lifecycle.label.softDeleted');
    case 'PURGED': return t('view.lifecycle.label.purged');
    default: return state;
  }
}

/**
 * Item 7 (third batch) — what a PENDING_BALANCE refusal's `details.balances`
 * looks like on screen, one line per brand that still owes this affiliate
 * something. Same shape affdice-admin's own DisableAffiliate modal already
 * renders (screens.ts there) — kept as a small local copy rather than a
 * cross-app import, since neither app may import the other's src.
 */
export function pendingBalanceLines(
  balances: readonly { brand_id: string; brand: string; currency: string; payable_minor: string }[],
): { brandId: string; brand: string; amount: string }[] {
  return balances.map((b) => ({
    brandId: b.brand_id,
    brand: b.brand,
    amount: formatMoney({ amount_minor: b.payable_minor, currency: b.currency }),
  }));
}

/**
 * The AffDice SuperAdmin's own headline — what `affdice-admin`'s Dashboard is
 * to one tenant, this is to all of them. Cross-tenant health and billing,
 * never an operator's own affiliate-marketing KPIs (§3.10b) — those live in
 * each operator's own console, and a platform view that showed them would
 * be reading commercial terms it has no business reading.
 */
export function platformDashboard(
  input: {
    overview: { data: PlatformOverviewData; data_as_of: string; unavailable_because?: string };
    operatorsByState: { by_state: OperatorLifecycleCount[]; blocked_on_go_live: number };
    chainResult: { failures: ChainFailure[]; records_checked: string; chains_checked: string; verdict: string };
    consumerRows: readonly ConsumerRow[];
    consumersCheckedAt: string;
    activity: { data: PlatformActivityData | null; unavailable_because?: string };
    billingRows: readonly PlatformBillingRow[];
  },
  now: Date,
): PlatformDashboardView {
  const byState = new Map(input.operatorsByState.by_state.map((s) => [s.state, s.count]));
  const liveCount = byState.get('LIVE') ?? 0;
  const daily = input.activity.data?.daily ?? [];

  const tenantPortfolio: PlatformKpiFigure[] = [
    withSpark({ label: t('view.dashboard.operators'), value: String(input.overview.data.operators_total) }, sparkSeries(daily, 'operators')),
    { label: lifecycleLabel('LIVE'), value: String(liveCount) },
    ...[...byState.entries()]
      .filter(([state]) => state !== 'LIVE')
      .map(([state, count]) => ({ label: lifecycleLabel(state), value: String(count) })),
    withSpark(
      { label: t('view.dashboard.affiliates'), value: String(input.overview.data.affiliates_total) },
      sparkSeries(daily, 'affiliates'),
    ),
  ];

  const goLive: PlatformKpiFigure[] = [
    {
      label: t('view.dashboard.blockedGoLive'),
      value: String(input.operatorsByState.blocked_on_go_live),
      ...(input.operatorsByState.blocked_on_go_live > 0 ? { tone: 'caution' as const } : {}),
    },
  ];

  const chainView = chain(input.chainResult);
  const consumerLines = input.consumerRows.map((r) => consumerLine(r, new Date(input.consumersCheckedAt)));
  const notMoving = consumerLines.filter((l) => l.state !== 'MOVING').length;
  const health: PlatformKpiFigure[] = [
    {
      label: t('view.dashboard.auditChain'),
      value: chainView.verified ? t('view.dashboard.verifies') : t('view.dashboard.notVerifies'),
      ...(chainView.verified ? {} : { tone: 'caution' as const }),
    },
    { label: t('view.dashboard.consumersMoving'), value: String(consumerLines.length - notMoving) },
    {
      label: t('view.dashboard.consumersNotMoving'),
      value: String(notMoving),
      ...(notMoving > 0 ? { tone: 'caution' as const } : {}),
    },
    // §34.2a's pattern again: no platform-wide ingest metering exists yet.
    { label: t('view.dashboard.eventsIngested'), value: '—' },
  ];

  // §39.2a Layer C — the only figure group here with no live fallback at
  // all: clicks/registrations/FTDs summed across every tenant come from
  // `platform_daily_kpis` alone, or not at all.
  const a = input.activity.data;
  const activity: PlatformKpiFigure[] = a
    ? [
        withSpark(
          { metric: 'clicks' as const, label: t('view.dashboard.clicksDays', { days: a.days }), value: formatCount(a.clicks) },
          sparkSeries(a.daily, 'clicks'),
        ),
        withSpark(
          { metric: 'registrations' as const, label: t('view.dashboard.registrationsDays', { days: a.days }), value: formatCount(a.registrations) },
          sparkSeries(a.daily, 'registrations'),
        ),
        withSpark(
          { metric: 'ftds' as const, label: t('view.dashboard.ftdsDays', { days: a.days }), value: formatCount(a.ftds) },
          sparkSeries(a.daily, 'ftds'),
        ),
      ]
    : [
        { metric: 'clicks' as const, label: t('view.figure.clicks'), value: '—', tone: 'caution' as const },
        { metric: 'registrations' as const, label: t('view.figure.registrations'), value: '—', tone: 'caution' as const },
        { metric: 'ftds' as const, label: t('view.figure.ftds'), value: '—', tone: 'caution' as const },
      ];

  return {
    updated: freshness(input.overview.data_as_of, now),
    tenantPortfolio,
    goLive,
    health,
    activity,
    ...(input.activity.unavailable_because ? { activityNote: input.activity.unavailable_because } : {}),
    billing: input.billingRows.map((r) => ({
      currency: r.currency,
      issued: formatMoney({ amount_minor: r.issued_minor, currency: r.currency }),
      paid: formatMoney({ amount_minor: r.paid_minor, currency: r.currency }),
      outstanding: formatMoney({ amount_minor: r.outstanding_minor, currency: r.currency }),
    })),
    billingNote: t('view.dashboard.billingNote'),
  };
}

/* ------------------------------------------------------------------ *
 * Q6 — a rejected CSV import
 * ------------------------------------------------------------------ */

export interface RefImportFailure {
  readonly line: number;
  readonly code: string;
  readonly problem: string;
}

/**
 * The per-row failures out of a rejected import, or an empty list.
 *
 * The server validates every row in one pass and refuses the file whole, so
 * the useful thing to show is every line at once — a person fixing a
 * spreadsheet one error per upload stops using the feature and pastes the
 * rows in by hand. Anything that is not that shape (a 500, a lost session, a
 * network failure) returns nothing here and is shown as an ordinary error,
 * because it is one.
 */
export function importFailuresOf(error: unknown): readonly RefImportFailure[] {
  const details = (error as { details?: unknown } | null)?.details;
  const raw = (details as { failures?: unknown } | undefined)?.failures;
  if (!Array.isArray(raw)) return [];

  const rows: RefImportFailure[] = [];
  for (const item of raw) {
    const f = item as { line?: unknown; code?: unknown; problem?: unknown };
    // A row with no problem to state is not one worth rendering an empty
    // table cell for; the line number alone tells nobody what to change.
    if (typeof f.problem !== 'string' || f.problem === '') continue;
    rows.push({
      line: typeof f.line === 'number' ? f.line : 0,
      code: typeof f.code === 'string' ? f.code : '',
      problem: f.problem,
    });
  }
  return rows;
}

/* ------------------------------------------------------------------ *
 * Reference data — searching a catalogue
 * ------------------------------------------------------------------ */

/**
 * Which reference-data rows a search box should be showing.
 *
 * Client-side on purpose. Every one of the five catalogues is already fully
 * loaded before the box exists — `refDataList` fetches the whole table,
 * inactive rows included, and there is no paging — so a round trip could only
 * re-fetch rows the screen is already holding, and would additionally answer
 * a *different* question from the one on screen if a row changed in between.
 * There is no server-side search route for these tables either; adding one to
 * filter a few dozen rows would be a route nobody needs.
 *
 * Here rather than beside the component for the same reason `visibleOptions`
 * is: the component is `.tsx` and a rendering harness would be testing React.
 * The decision worth pinning is what counts as a match — the code, and the
 * text columns the table is showing, so that what someone types is checked
 * against what they can see. Non-text columns (a minor-unit scale, a yes/no
 * flag) are not searched: matching "2" against every scale, or "true" against
 * a boolean nobody rendered as the word "true", finds rows for reasons the
 * person cannot see.
 */
export function refRowsMatching<T extends Record<string, unknown>>(
  rows: readonly T[],
  query: string,
  textFieldKeys: readonly string[],
): readonly T[] {
  // Trimmed, because a trailing space survives every copy-paste and a search
  // that answers "nothing" to a pasted code is indistinguishable from a
  // catalogue that does not have it.
  const needle = query.trim().toLowerCase();
  if (needle === '') return rows;

  return rows.filter((row) =>
    ['code', ...textFieldKeys].some((key) => {
      const value = row[key];
      return typeof value === 'string' && value.toLowerCase().includes(needle);
    }),
  );
}

/**
 * The PUT body for a reference-data row's Edit save — every field's current
 * draft value, except a field marked `readOnlyOnEdit`.
 *
 * A jurisdiction's `country` and `subdivision` are that: 0002's CHECK ties
 * `code` to both (`(subdivision IS NULL) = (code = country)`), so the API
 * refuses a save that tries to change either on a row that already has a
 * code. `editValues` still carries them — Edit seeds it from the row so a
 * field left alone round-trips its own value — this only decides what the
 * save actually offers back.
 */
export function refEditPayload(
  fields: readonly { key: string; readOnlyOnEdit?: boolean }[],
  values: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const f of fields) {
    if (f.readOnlyOnEdit) continue;
    payload[f.key] = values[f.key];
  }
  return payload;
}

/* ------------------------------------------------------------------ *
 * Deleting a period — §22.6, and the one case it does not cover
 * ------------------------------------------------------------------ */
/**
 * "1 statement", "3 statements".
 *
 * Moved here from App.tsx, which had it as a file-local, when the period
 * deletion view below needed the same thing: a count and its noun agreeing is
 * not a rendering concern, and two copies is how "1 invoices" eventually
 * appears on one of the two screens.
 */
export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}


export interface PeriodTally {
  readonly label: string;
  readonly count: number;
}

export interface PeriodDeletionView {
  /** What the caller has to type, exactly as the server will compare it. */
  readonly phrase: string;
  /** Removed by the delete, largest first — the counts that are the warning. */
  readonly goes: readonly PeriodTally[];
  /** Kept, with the pointer into this period dropped. */
  readonly stays: readonly PeriodTally[];
  /** §28.4 corrections booked elsewhere that will stop naming what they corrected. */
  readonly unlinked: number;
  readonly severity: 'empty' | 'computed' | 'documented';
  readonly summary: string;
}

/**
 * What deleting one period would actually do, said in that period's numbers.
 *
 * A confirmation that warns in the abstract — "this cannot be undone" — is one
 * people click through, because it says the same thing whether the period
 * holds nothing at all or holds forty statements an affiliate has already been
 * sent. Those are different acts and the dialog should not read identically.
 *
 * So three severities, and the line between them is whether a document left
 * the building:
 *
 *  - `empty` — nothing was ever computed here. Removing it is housekeeping,
 *    and saying so stops the dialog crying wolf on the case it is used for
 *    most.
 *  - `computed` — there are figures, and they are the platform's own. They
 *    come back when the month is calculated again.
 *  - `documented` — statements or invoices were issued, which under §30.4c
 *    means the period was locked and the affiliate has the figures. Those do
 *    not come back by recomputing: the document numbers were consumed, and
 *    whoever received them holds a document the platform no longer has.
 *
 * `unlinked` is called out on its own at every severity, because it is the
 * only part of this that recomputing does not put back: a §28.4 correction
 * booked into a later period keeps its figure and loses the pointer to what it
 * corrected. Nothing else on the screen would show it.
 *
 * Zero counts are dropped rather than rendered as "0 statements" — a list of
 * zeroes is how the one non-zero row stops being noticed.
 */
export function periodDeletion(row: {
  readonly confirm_phrase: string;
  readonly commission_lines: number;
  readonly statements: number;
  readonly invoices: number;
  readonly payout_documents: number;
  readonly disputes_kept: number;
  readonly platform_invoices_kept: number;
  readonly corrections_unlinked: number;
}): PeriodDeletionView {
  const goes = ([
    { label: (n: number) => tCount('view.period.commissionLine', n), count: row.commission_lines },
    { label: (n: number) => tCount('view.period.statement', n), count: row.statements },
    { label: (n: number) => tCount('view.period.invoice', n), count: row.invoices },
    { label: (n: number) => tCount('view.period.payoutDocument', n), count: row.payout_documents },
  ] as const)
    .filter((item) => item.count > 0)
    .map((item) => ({ label: item.label(item.count), count: item.count }));

  const stays = ([
    { label: (n: number) => tCount('view.period.dispute', n), count: row.disputes_kept },
    { label: (n: number) => tCount('view.period.platformInvoice', n), count: row.platform_invoices_kept },
  ] as const)
    .filter((item) => item.count > 0)
    .map((item) => ({ label: item.label(item.count), count: item.count }));

  const documented = row.statements > 0 || row.invoices > 0 || row.payout_documents > 0;
  const severity = documented ? 'documented' : goes.length > 0 ? 'computed' : 'empty';

  const summary = documented
    ? t('view.period.summaryDocumented')
    : severity === 'computed'
      ? t('view.period.summaryComputed')
      : t('view.period.summaryEmpty');

  return {
    phrase: row.confirm_phrase,
    goes,
    stays,
    unlinked: row.corrections_unlinked,
    severity,
    summary,
  };
}

/**
 * The chip tone for a period's state, which is also how far through the month
 * it is: OPEN and CLOSED still recompute (§22.7a), so they read provisional;
 * LOCKED and RESTATED do not.
 */
export function periodStateTone(state: string): 'neutral' | 'provisional' | 'locked' | 'overdue' {
  if (state === 'LOCKED') return 'locked';
  if (state === 'RESTATED') return 'overdue';
  return 'provisional';
}

/* ------------------------------------------------------------------ *
 * §12.2 — scheduled brand settings, and their history
 * ------------------------------------------------------------------ */

/**
 * §12.2 — the five click/bot keys the owner asked to read well together,
 * grouped here under "Click and bot limits" the way the operator/brand
 * console groups them too. `maxLevel: 'BRAND'` on all five (registry) is why
 * every one of them can appear on this screen.
 */
export const CLICK_AND_BOT_SETTING_KEYS: ReadonlySet<string> = new Set([
  'tracking.click_dedup_window_seconds',
  'tracking.click_velocity_per_hour',
  'tracking.retro_classification_window_hours',
  'fraud.bot_ratio_case_threshold_bps',
  'fraud.bot_ratio_min_clicks',
]);

/**
 * Splits a brand's settings into the click/bot group and everything else —
 * only when the click/bot group actually has something in it, so a registry
 * that stops sending one of the five does not leave a heading over an empty
 * table. Order within each group is left exactly as the API sent it.
 */
export function brandSettingGroups<T extends { key: string }>(
  items: readonly T[],
): readonly { title: string | null; items: readonly T[] }[] {
  const clickAndBot = items.filter((i) => CLICK_AND_BOT_SETTING_KEYS.has(i.key));
  if (clickAndBot.length === 0) return [{ title: null, items }];
  const rest = items.filter((i) => !CLICK_AND_BOT_SETTING_KEYS.has(i.key));
  return [
    { title: t('view.setting.groupClickBot'), items: clickAndBot },
    ...(rest.length > 0 ? [{ title: t('view.setting.groupOther'), items: rest }] : []),
  ];
}

/**
 * §12.2 — the four click/bot keys with a unit of their own, the same units
 * the operator/brand console's editor labels them with (`op.seconds`,
 * `op.hours`, `op.clicksPerHour`, `op.clicks`). `fraud.bot_ratio_case_threshold_bps`
 * needs no entry here — it already reads as a percentage from its `_bps`
 * suffix, below.
 */
function clickAndBotLimitWithUnit(key: string, value: string): string | undefined {
  switch (key) {
    case 'tracking.click_dedup_window_seconds': return t('view.setting.seconds', { value });
    case 'tracking.click_velocity_per_hour': return t('view.setting.clicksPerHour', { value });
    case 'tracking.retro_classification_window_hours': return t('view.setting.hours', { value });
    case 'fraud.bot_ratio_min_clicks': return t('view.setting.clicks', { value });
    default: return undefined;
  }
}

/**
 * §12.2 — a raw setting value, in the units the reader thinks in rather than
 * the wire's own encoding: a `_bps` key reads as a percentage (`3000` →
 * "30.00%") and the four click/bot keys above carry their own unit — the same
 * per-key kind the operator console's `settingControl` derives for its
 * editor, applied here to what a reader is simply shown. `show(row.value)`
 * used to print a bot-ratio threshold as a bare "3000" and a dedup window as
 * a bare "5", neither of which says what the number means. One function, used
 * everywhere a value is shown on this screen: the settings table, the
 * scheduled-change line under it, and the History dialog.
 *
 * An object value (money, mostly) is left to `JSON.stringify` exactly as
 * `show()` always has — no finding asked for money to read differently here,
 * and guessing at a shape not named in the review is how a fix grows past
 * what was verified.
 */
export function formatSettingValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  const withUnit = clickAndBotLimitWithUnit(key, String(value));
  if (withUnit !== undefined) return withUnit;
  if (typeof value === 'number' && key.endsWith('_bps')) return bpsPercent(value);
  return String(value);
}

/**
 * §12.2 — the sentence under a setting that already has a change on the way:
 * "Changes to 10 on 04-01-2026." `value` is the already-formatted display
 * string (`formatSettingValue`'s own), so this stays agnostic of what kind of
 * setting it is.
 *
 * Owner, 2026-10-12 — the day is read on the reader's own calendar, like
 * every other time on screen. `effective_from` is the UTC instant the change
 * starts; slicing its ISO string would print the UTC day instead.
 */
export function scheduledChangeLine(value: string, effectiveFrom: string): string {
  return t('view.setting.changesTo', { value, day: formatDay(localDayOf(effectiveFrom)) });
}

/**
 * §12.2 / §4.2d — the state a history row is in, as the chip tone that
 * carries it: a scheduled row is still moving (provisional), one in force is
 * settled (locked), and one that has ended or was withdrawn before it ever
 * started is neither — neutral, the same as every other "not current" state
 * this console shows.
 */
export function settingHistoryTone(
  state: 'SCHEDULED' | 'IN_FORCE' | 'ENDED' | 'WITHDRAWN',
): 'provisional' | 'locked' | 'neutral' {
  if (state === 'SCHEDULED') return 'provisional';
  if (state === 'IN_FORCE') return 'locked';
  return 'neutral';
}

/** The state chip's own words — plain, not the constant's own shouting case. */
export function settingHistoryStateLabel(
  state: 'SCHEDULED' | 'IN_FORCE' | 'ENDED' | 'WITHDRAWN',
): string {
  switch (state) {
    case 'SCHEDULED': return t('view.setting.state.scheduled');
    case 'IN_FORCE': return t('view.setting.state.inForce');
    case 'ENDED': return t('view.setting.state.ended');
    case 'WITHDRAWN': return t('view.setting.state.withdrawn');
  }
}

/* ------------------------------------------------------------------ *
 * §12.3 — sub-affiliate networks, read-only
 * ------------------------------------------------------------------ */

/**
 * §12.3 — the operator's programme, in one line: whether it is on, how deep
 * it runs, and the default and ceiling every recruiter's own rate sits
 * inside. A recruiter's actual rate is on their own row, not here — this is
 * the shape of the programme, not any one relationship in it.
 */
export function referralProgrammeSummary(programme: {
  readonly enabled: boolean;
  readonly depth: number;
  readonly cap_bps: number;
  readonly default_override_bps: number;
}): string {
  if (!programme.enabled) {
    return t('view.referral.off');
  }
  return tCount('view.referral.on', programme.depth, {
    override: bpsPercent(programme.default_override_bps),
    cap: bpsPercent(programme.cap_bps),
  });
}

/** Basis points as a percentage string: 500 → "5.00%". */
export function bpsPercent(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}

/**
 * §12.3 — an override's rate with its basis unmissable in the same cell,
 * since "5.00%" alone answers a different question depending on which of a
 * sub-affiliate's figures it is a share of.
 */
export function referralOverrideCell(
  bps: number,
  basis: 'SUB_COMMISSION' | 'SUB_NGR',
): string {
  const rate = bpsPercent(bps);
  return basis === 'SUB_NGR' ? t('view.referral.ofNgr', { rate }) : t('view.referral.ofCommission', { rate });
}

/** The lifetime policy's own words — plain, not the constant's own shouting case. */
export function lifetimePolicyLabel(
  policy: 'FIXED_TERM' | 'LIFETIME' | 'ACTIVE_ONLY',
): string {
  switch (policy) {
    case 'FIXED_TERM': return t('view.referral.fixedTerm');
    case 'LIFETIME': return t('view.referral.lifetime');
    case 'ACTIVE_ONLY': return t('view.referral.whileActive');
  }
}

/* ------------------------------------------------------------------ *
 * The affiliate list's column ordering
 * ------------------------------------------------------------------ */

/*
 * The columns this list can be ordered by, and the server sort key each one
 * asks for. There was a Sort dropdown here offering the same seven orderings
 * the four column headings now offer — a second control for what a heading
 * already says, which is the duplication the owner pointed at on the traffic
 * report ("khali group by more deto ani vrti same ch option").
 *
 * The ordering stays on the server. This query answers `LIMIT 500`, so
 * reordering the rows in the browser would order the 500 the server chose
 * under some other sort, and "most clicks" would mean "most clicks among the
 * first 500 by name". `useSort` is therefore not what drives this table.
 */
const AFFILIATE_SORT_COLUMNS = {
  name: { asc: 'name', desc: '-name' },
  operator: { asc: 'operator', desc: '-operator' },
  clicks: { asc: 'clicks', desc: '-clicks' },
  joined: { asc: 'joined', desc: '-joined' },
} as const;

type AffiliateSortCol = keyof typeof AFFILIATE_SORT_COLUMNS;

/**
 * A `SortState` for `SortTh` that reads and writes the server's sort string,
 * so the headings look and behave like every other sortable table in the
 * three consoles while the database does the ordering.
 */
export function serverSort(
  sort: string,
  setSort: (next: string) => void,
): SortState<AffiliateSortCol> {
  const entries = Object.entries(AFFILIATE_SORT_COLUMNS) as [AffiliateSortCol, { asc: string; desc: string }][];
  const found = entries.find(([, v]) => v.asc === sort || v.desc === sort);
  // `active` is the older name for clicks-descending and still a valid value.
  const key = found?.[0] ?? (sort === 'active' ? 'clicks' : null);
  const dir: SortDir = found
    ? (found[1].asc === sort ? 'asc' : 'desc')
    : (sort === 'active' ? 'desc' : 'asc');
  return {
    key,
    dir,
    by: (col) => {
      const pair = AFFILIATE_SORT_COLUMNS[col];
      // Same column flips; a new one starts the way that column reads best —
      // names and operators A–Z, figures and dates with the largest first.
      if (key === col) { setSort(dir === 'asc' ? pair.desc : pair.asc); return; }
      setSort(col === 'clicks' || col === 'joined' ? pair.desc : pair.asc);
    },
  };
}
